/**
 * DISCOM circle-dot overlay map.
 * Renders Gujarat choropleth (same base as GujaratMap) with
 * coloured circle dots positioned at each circle's centroid.
 */
import React, { useState } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from 'react-simple-maps';
import { gujaratDistricts, pgvclCircleCentroids, CircleCentroid } from '../../data/mockData';

const GEO_URL = '/gujarat-districts.topo.json';

const SHADES  = ['#DBEAFE', '#93C5FD', '#3B82F6', '#1D4ED8', '#1E3A8A'];
const NO_DATA = '#CBD5E1';

function getShade(value: number | undefined, min: number, max: number): string {
  if (value === undefined) return NO_DATA;
  const norm = (value - min) / (max - min || 1);
  return SHADES[Math.min(4, Math.floor(norm * 5))];
}

function darken(hex: string, factor = 0.82): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const c = (v: number) =>
    Math.round(Math.min(255, v * factor)).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

const GEO_TO_MOCK: Record<string, string> = {
  'Ahmadabad':    'Ahmedabad',
  'Surat':        'Surat',
  'Vadodara':     'Vadodara',
  'Rajkot':       'Rajkot',
  'Junagadh':     'Junagadh',
  'Gandhinagar':  'Gandhinagar',
  'Anand':        'Anand',
  'Kheda':        'Kheda',
  'Mahesana':     'Mahesana',
  'Patan':        'Patan',
  'Banas Kantha': 'Banas Kantha',
  'Sabar Kantha': 'Sabar Kantha',
  'Panch Mahals': 'Panch Mahals',
  'Dahod':        'Dahod',
  'Narmada':      'Narmada',
  'Bharuch':      'Bharuch',
  'Kachchh':      'Kachchh',
  'Jamnagar':     'Jamnagar',
  'Porbandar':    'Porbandar',
  'Amreli':       'Amreli',
  'Bhavnagar':    'Bhavnagar',
  'Surendranagar':'Surendranagar',
  'Navsari':      'Navsari',
  'Valsad':       'Valsad',
  'The Dangs':    'The Dangs',
};

// dot colour scale: green shades for generation intensity
const DOT_COLORS = ['#BBF7D0', '#34D399', '#10B981', '#059669', '#064E3B'];

function getDotColor(value: number, min: number, max: number): string {
  const norm = (value - min) / (max - min || 1);
  return DOT_COLORS[Math.min(4, Math.floor(norm * 5))];
}

function getDotRadius(plants: number, maxPlants: number): number {
  return 6 + Math.round((plants / maxPlants) * 10);
}

interface DiscomCircleMapProps {
  onCircleSelect?: (id: string | null) => void;
  selectedCircle?: string | null;
}

export default function DiscomCircleMap({
  onCircleSelect,
  selectedCircle,
}: DiscomCircleMapProps) {
  const [hovered, setHovered]   = useState<string | null>(null);
  const [tooltip, setTooltip]   = useState<{ x: number; y: number; text: string } | null>(null);

  const distMap = Object.fromEntries(gujaratDistricts.map(d => [d.name, d]));
  const values  = gujaratDistricts.map(d => d.generationGWh);
  const minVal  = Math.min(...values);
  const maxVal  = Math.max(...values);

  const genValues   = pgvclCircleCentroids.map(c => c.generationMWh);
  const minGen      = Math.min(...genValues);
  const maxGen      = Math.max(...genValues);
  const maxPlants   = Math.max(...pgvclCircleCentroids.map(c => c.plants));

  return (
    <div className="relative w-full h-full flex flex-col">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: 3800, center: [71.5, 22.4] }}
        style={{ width: '100%', height: '100%' }}
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map(geo => {
              const geoName  = geo.properties.NAME_2 as string;
              const mockName = GEO_TO_MOCK[geoName] ?? geoName;
              const data     = distMap[mockName];
              const fill     = getShade(data?.generationGWh, minVal, maxVal);
              const isHover  = hovered === geoName;
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={isHover ? darken(fill) : fill}
                  stroke="#FFFFFF"
                  strokeWidth={0.5}
                  style={{
                    default: { outline: 'none' },
                    hover:   { outline: 'none' },
                    pressed: { outline: 'none' },
                  }}
                  onMouseEnter={() => setHovered(geoName)}
                  onMouseLeave={() => setHovered(null)}
                />
              );
            })
          }
        </Geographies>

        {/* Circle dots */}
        {pgvclCircleCentroids.map(circle => {
          const isSelected = selectedCircle === circle.id;
          const color      = getDotColor(circle.generationMWh, minGen, maxGen);
          const r          = getDotRadius(circle.plants, maxPlants);

          return (
            <Marker
              key={circle.id}
              coordinates={[circle.lng, circle.lat]}
              onClick={() => {
                if (onCircleSelect) {
                  onCircleSelect(isSelected ? null : circle.id);
                }
              }}
              onMouseEnter={(e: React.MouseEvent<SVGGElement>) => {
                const rect = (e.target as SVGElement)
                  .closest('svg')!.getBoundingClientRect();
                setTooltip({
                  x: e.clientX - rect.left + 12,
                  y: e.clientY - rect.top - 36,
                  text: `${circle.name}: ${circle.generationMWh} MWh · ${circle.plants} plants`,
                });
              }}
              onMouseMove={(e: React.MouseEvent<SVGGElement>) => {
                const rect = (e.target as SVGElement)
                  .closest('svg')!.getBoundingClientRect();
                setTooltip(prev =>
                  prev ? { ...prev, x: e.clientX - rect.left + 12, y: e.clientY - rect.top - 36 }
                       : null
                );
              }}
              onMouseLeave={() => setTooltip(null)}
            >
              <circle
                r={r}
                fill={color}
                stroke={isSelected ? '#1D4ED8' : '#FFFFFF'}
                strokeWidth={isSelected ? 3 : 1.5}
                style={{ cursor: 'pointer', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.3))' }}
              />
              <text
                textAnchor="middle"
                dy={r + 9}
                style={{ fontSize: 8, fill: '#374151', fontWeight: 600, pointerEvents: 'none' }}
              >
                {circle.name}
              </text>
            </Marker>
          );
        })}
      </ComposableMap>

      {/* Floating tooltip */}
      {tooltip && (
        <div
          className="absolute z-50 px-2.5 py-1.5 rounded text-white text-xs shadow-lg pointer-events-none"
          style={{
            left: tooltip.x,
            top:  tooltip.y,
            background: 'rgba(15,23,42,0.90)',
            whiteSpace: 'nowrap',
          }}
        >
          {tooltip.text}
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-2 mt-2 px-1">
        <span className="text-xs text-text-tertiary">District intensity</span>
        <div className="flex gap-0.5">
          {SHADES.map((c, i) => (
            <div key={i} className="w-5 h-2 rounded-sm" style={{ backgroundColor: c }} />
          ))}
        </div>
        <span className="text-xs text-text-tertiary ml-3">Circle generation</span>
        <div className="flex gap-0.5">
          {DOT_COLORS.map((c, i) => (
            <div key={i} className="w-3 h-3 rounded-full" style={{ backgroundColor: c }} />
          ))}
        </div>
        <span className="text-xs text-text-tertiary">(Low → High)</span>
      </div>
    </div>
  );
}
